import React, { useEffect, useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Mail, Send, Clock, Users, UserMinus, Save, History, FlaskConical } from 'lucide-react';
import { toast } from 'sonner';

const DAYS = [
  { v: 0, label: 'Monday' }, { v: 1, label: 'Tuesday' }, { v: 2, label: 'Wednesday' },
  { v: 3, label: 'Thursday' }, { v: 4, label: 'Friday' },
  { v: 5, label: 'Saturday' }, { v: 6, label: 'Sunday' },
];

const SECTION_LABELS = {
  welcome_members: 'Welcome new members',
  top_posts: 'Top 5 most active posts',
  news: 'Latest news',
  courses: 'New courses (only if added this week)',
  events: 'Active events',
};

function fmtDateTime(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' }); }
  catch { return iso; }
}

export default function WeeklyDigestSubTab() {
  const [cfg, setCfg] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [sendingNow, setSendingNow] = useState(false);
  const [confirmSend, setConfirmSend] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [cRes, hRes] = await Promise.all([
        fetch('/api/admin/weekly-digest/config', { credentials: 'include' }),
        fetch('/api/admin/weekly-digest/history?limit=20', { credentials: 'include' }),
      ]);
      if (!cRes.ok) throw new Error('config fetch failed');
      setCfg(await cRes.json());
      setHistory(hRes.ok ? await hRes.json() : []);
    } catch (e) {
      toast.error('Failed to load weekly-digest config');
    }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!cfg) { return; }
    setSaving(true);
    try {
      const r = await fetch('/api/admin/weekly-digest/config', {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          enabled: cfg.enabled,
          send_day: cfg.send_day,
          send_time: cfg.send_time,
          subject: cfg.subject,
          intro_paragraph: cfg.intro_paragraph,
          sections: cfg.sections,
        }),
      });
      const d = await r.json();
      if (!r.ok) { throw new Error(d.detail || 'save failed'); }
      setCfg({ ...cfg, ...d });
      toast.success('Weekly digest settings saved');
    } catch (e) {
      toast.error(e.message || 'Failed to save settings');
    }
    setSaving(false);
  };

  const sendTest = async () => {
    setSendingTest(true);
    try {
      const r = await fetch('/api/admin/weekly-digest/send-test', { method: 'POST', credentials: 'include' });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'failed');
      toast.success(`Test sent to ${d.to}`);
    } catch (e) { toast.error(e.message || 'Test failed'); }
    setSendingTest(false);
  };

  const sendNow = async () => {
    setSendingNow(true);
    try {
      const r = await fetch('/api/admin/weekly-digest/send-now', { method: 'POST', credentials: 'include' });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'failed');
      toast.success(`Digest sent to ${d.recipient_count} member${d.recipient_count === 1 ? '' : 's'}`);
      setConfirmSend(false);
      load();
    } catch (e) { toast.error(e.message || 'Send failed'); }
    setSendingNow(false);
  };

  if (loading || !cfg) return <div className="text-slate-400 text-center py-12">Loading…</div>;

  return (
    <div className="space-y-6" data-testid="weekly-digest-subtab">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold font-heading flex items-center gap-2">
            <Mail className="h-5 w-5 text-brand-navy" /> Weekly Email Digest
          </h2>
          <p className="text-xs md:text-sm text-slate-500 mt-1">
            One round-up email sent on a recurring schedule. Members can opt out from the email itself or their notification preferences.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={sendTest} disabled={sendingTest} data-testid="wd-send-test">
            <FlaskConical className="h-4 w-4 mr-1" /> {sendingTest ? 'Sending…' : 'Send test to me'}
          </Button>
          <Button size="sm" variant="outline" className="border-rose-200 text-rose-700 hover:bg-rose-50"
            onClick={() => setConfirmSend(true)} disabled={sendingNow} data-testid="wd-send-now">
            <Send className="h-4 w-4 mr-1" /> Send now
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="p-4">
          <p className="text-xs text-slate-500 uppercase tracking-wide flex items-center gap-1"><Users className="h-3.5 w-3.5" /> Recipients</p>
          <p className="text-2xl font-bold tabular-nums" data-testid="wd-recipient-count">{cfg.recipient_count?.toLocaleString() ?? '—'}</p>
        </Card>
        <Card className="p-4">
          <p className="text-xs text-slate-500 uppercase tracking-wide flex items-center gap-1"><UserMinus className="h-3.5 w-3.5" /> Opted out</p>
          <p className="text-2xl font-bold tabular-nums text-amber-600" data-testid="wd-optout-count">{cfg.opted_out_count?.toLocaleString() ?? '—'}</p>
        </Card>
        <Card className="p-4">
          <p className="text-xs text-slate-500 uppercase tracking-wide flex items-center gap-1"><History className="h-3.5 w-3.5" /> Last sent</p>
          <p className="text-sm font-semibold mt-1">{fmtDateTime(cfg.last_sent_at)}</p>
        </Card>
        <Card className="p-4">
          <p className="text-xs text-slate-500 uppercase tracking-wide flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> Next send</p>
          <p className="text-sm font-semibold mt-1">{fmtDateTime(cfg.next_send_at)}</p>
        </Card>
      </div>

      {/* Config form */}
      <Card className="p-5 space-y-5">
        <div className="flex items-center justify-between border-b border-slate-100 pb-4">
          <div>
            <p className="text-sm font-semibold text-slate-800">Master toggle</p>
            <p className="text-xs text-slate-500">When off, no scheduled digests are sent. &ldquo;Send now&rdquo; still works for admins.</p>
          </div>
          <Switch checked={!!cfg.enabled} onCheckedChange={(v) => setCfg({ ...cfg, enabled: v })} data-testid="wd-enabled-toggle" />
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <Label className="text-xs font-semibold text-slate-500 mb-1.5 block">Send day</Label>
            <Select value={String(cfg.send_day)} onValueChange={(v) => setCfg({ ...cfg, send_day: parseInt(v, 10) })}>
              <SelectTrigger data-testid="wd-day-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {DAYS.map(d => <SelectItem key={d.v} value={String(d.v)}>{d.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs font-semibold text-slate-500 mb-1.5 block">Send time (Europe/London)</Label>
            <Input type="time" value={cfg.send_time}
              onChange={(e) => setCfg({ ...cfg, send_time: e.target.value })}
              data-testid="wd-time-input" />
          </div>
        </div>

        <div>
          <Label className="text-xs font-semibold text-slate-500 mb-1.5 block">Email subject</Label>
          <Input value={cfg.subject || ''} onChange={(e) => setCfg({ ...cfg, subject: e.target.value })}
            data-testid="wd-subject-input" />
        </div>

        <div>
          <Label className="text-xs font-semibold text-slate-500 mb-1.5 block">Intro paragraph</Label>
          <Textarea value={cfg.intro_paragraph || ''} rows={3}
            onChange={(e) => setCfg({ ...cfg, intro_paragraph: e.target.value })}
            data-testid="wd-intro-input" />
        </div>

        <div>
          <p className="text-sm font-semibold text-slate-800 mb-2">Sections to include</p>
          <div className="space-y-2">
            {Object.keys(SECTION_LABELS).map(k => (
              <div key={k} className="flex items-center justify-between py-2 border-b border-slate-50 last:border-0">
                <span className="text-sm text-slate-700">{SECTION_LABELS[k]}</span>
                <Switch
                  data-testid={`wd-section-${k}`}
                  checked={cfg.sections?.[k] !== false}
                  onCheckedChange={(v) => setCfg({ ...cfg, sections: { ...(cfg.sections || {}), [k]: v } })}
                />
              </div>
            ))}
          </div>
        </div>

        <div className="flex justify-end pt-2">
          <Button onClick={save} disabled={saving} className="bg-brand-navy hover:bg-brand-dark text-white" data-testid="wd-save-btn">
            <Save className="h-4 w-4 mr-1" /> {saving ? 'Saving…' : 'Save settings'}
          </Button>
        </div>
      </Card>

      {/* History */}
      <Card className="p-5">
        <h3 className="text-sm font-bold uppercase tracking-wide text-slate-700 mb-3 flex items-center gap-2">
          <History className="h-4 w-4" /> Send history
        </h3>
        {history.length === 0 ? (
          <p className="text-slate-400 text-sm">No digests sent yet.</p>
        ) : (
          <div className="divide-y divide-slate-100" data-testid="wd-history-list">
            {history.map((h) => (
              <div key={h.digest_id} className="py-2.5 flex items-center justify-between gap-3 text-sm">
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-slate-800 truncate">{h.subject}</p>
                  <p className="text-xs text-slate-500">
                    {fmtDateTime(h.sent_at)} · sent to {h.recipient_count}
                    {h.failed_count ? ` · ${h.failed_count} failed` : ''}
                    {h.opted_out_count != null ? ` · ${h.opted_out_count} opted out` : ''}
                    {h.triggered_by ? ` · trigger: ${h.triggered_by}` : ''}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Confirm send-now */}
      {confirmSend && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={() => setConfirmSend(false)}>
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-5 m-4" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-bold mb-2 flex items-center gap-2">
              <Send className="h-5 w-5 text-rose-600" /> Send digest now?
            </h3>
            <p className="text-sm text-slate-600 mb-4">
              This will immediately email the weekly digest to <strong>{cfg.recipient_count}</strong> recipient{cfg.recipient_count === 1 ? '' : 's'}.
              It cannot be recalled once sent.
            </p>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setConfirmSend(false)} disabled={sendingNow}>Cancel</Button>
              <Button onClick={sendNow} disabled={sendingNow} className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="wd-confirm-send-now">
                {sendingNow ? 'Sending…' : 'Yes, send now'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
